import { motion } from "framer-motion";
import { Lightbulb, Eye, Target, CheckCircle2, ArrowRight } from "lucide-react";

/**
 * VisionMissionSection — React + Tailwind + Framer Motion
 *
 * Props (all optional):
 * - heading: string
 * - subheading: string
 * - visionTitle: string
 * - visionText: string
 * - missionTitle: string
 * - missionPoints: string[]
 * - ctaText: string
 * - onCtaClick: () => void
 *
 * Example usage:
 * <VisionMissionSection
 *   heading="Tentang Kami"
 *   subheading="Menjadi solusi transportasi terpercaya."
 *   visionTitle="Visi"
 *   visionText="Menjadi penyedia layanan transportasi paling dipercaya di Medan dengan standar keselamatan dan kenyamanan tertinggi."
 *   missionTitle="Misi"
 *   missionPoints={[
 *     "Memberikan layanan tepat waktu dan profesional.",
 *     "Menjaga armada selalu prima dan terawat.",
 *     "Mengutamakan kepuasan pelanggan dalam setiap perjalanan.",
 *   ]}
 *   ctaText="Pelajari Lebih Lanjut"
 *   onCtaClick={() => console.log("CTA clicked")}
 * />
 */
export default function VisionMissionSection({
    heading = "Visi & Misi",
    subheading = "Arah, tujuan, dan komitmen kami.",
    visionTitle = "Visi",
    visionText =
    "Menjadi Perusahaan terbaik dan profesional didalam bidang pelayanan jasa transportasi darat serta menciptakan nilai plus bagi stakeholder tidak hanya dari sisi pemilik bisnis semata, namun juga dari seluruh pihak yang terkait dengan usaha ini termasuk karyawan dan pelanggan kami",
    missionTitle = "Misi",
    missionPoints = [
        "Meningkatkan kepuasan pelanggan dengan menetapkan kualitas layanan yang terbaik.",
        "Mengantisipasi kecenderungan pasar dan kebutuhan pelanggan dengan mempersiapkan sarana yang sesuai, berkualitas, aman dan nyaman.",
        "Mengembangkan secara berkesinambungan sumber daya manusia dalam bidang disiplin, komunikasi, pengetahuan wisata dan jaringan terkait, serta memberikan penghargaan atas prestasi kerja.",
        "Berusaha meningkatkan kemampuan laba Perusahaan",
        "Menerapkan tata Kelola Perusahaan yang baik."
    ],
    ctaText = "Pelajari Lebih Lanjut",
    onCtaClick,
}) {
    return (
        <section 
        className="relative overflow-hidden bg-gradient-to-br from-green-600 to-green-800  text-white py-20">
            {/* Decorative blobs */}
            <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />

            <div className="relative mx-auto max-w-6xl px-6">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-14"
                >
                    <p className="text-emerald-200/90 tracking-wide uppercase text-sm font-medium">
                        {subheading}
                    </p>
                    <h2 className="text-3xl md:text-5xl font-extrabold mt-2 drop-shadow-sm">
                        {heading}
                    </h2>
                </motion.div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Vision Card */}
                    <motion.div
                        initial={{ opacity: 0, x: -24 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5 }}
                        className="bg-white/10 backdrop-blur-sm rounded-2xl p-7 md:p-8 shadow-lg hover:bg-white/15 transition"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-xl bg-white/15"><Lightbulb className="h-6 w-6" /></div>
                            <h3 className="text-2xl font-bold">{visionTitle}</h3>
                        </div>
                        <p className="text-emerald-50/95 leading-relaxed text-base md:text-lg">
                            {visionText}
                        </p>
                    </motion.div>

                    {/* Mission Card */}
                    <motion.div
                        initial={{ opacity: 0, x: 24 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5, delay: 0.05 }}
                        className="bg-white/10 backdrop-blur-sm rounded-2xl p-7 md:p-8 shadow-lg hover:bg-white/15 transition"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-xl bg-white/15"><Target className="h-6 w-6" /></div>
                            <h3 className="text-2xl font-bold">{missionTitle}</h3>
                        </div>
                        <ul className="space-y-3">
                            {missionPoints.map((point, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                    <span className="mt-1"><CheckCircle2 className="h-5 w-5" /></span>
                                    <p className="text-emerald-50/95 leading-relaxed text-base">{point}</p>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>

                {/* CTA */}
                {/* {ctaText && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.45, delay: 0.1 }}
                        className="mt-10 flex justify-center"
                    >
                        <button
                            onClick={onCtaClick}
                            className="inline-flex items-center gap-2 rounded-2xl bg-white text-emerald-900 font-semibold px-5 py-3 shadow-md hover:shadow-lg hover:bg-emerald-50 active:scale-[0.99] transition"
                        >
                            {ctaText}
                            <ArrowRight className="h-5 w-5" />
                        </button>
                    </motion.div>
                )} */}
            </div>
        </section>
    );
}
